import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Circuitry, Lightning, ArrowLeft, ArrowsClockwise, Power, Spinner,
  User, Buildings, House, Warning, CurrencyCircleDollar,
  Thermometer, Gauge, BatteryCharging, Plus, Drop,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { getDeviceFreshness, getFreshnessDisplay, timeAgoTr } from "../utils/deviceFreshness";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DeviceDetailPage = () => {
  const { t } = useTranslation();
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isStaff = user?.role === "staff";
  const [detail, setDetail] = useState(null);
  const [realtimeData, setRealtimeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [chargeKwh, setChargeKwh] = useState("");
  const [topupTl, setTopupTl] = useState("");
  const [topupPreview, setTopupPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const hasPerm = (key) => !isStaff || user?.permissions?.[key] !== false;

  const fetchDetail = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/devices/${deviceId}/detail`);
      setDetail(res.data);
    } catch {
      toast.error("Cihaz detayi yuklenemedi");
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  const fetchRealtimeData = useCallback(async () => {
    setActionLoading((p) => ({ ...p, realtime: true }));
    try {
      const res = await axios.get(`${API_URL}/api/devices/${deviceId}/realtime-data`);
      if (res.data.success) {
        setRealtimeData(res.data);
        toast.success("Anlik veriler guncellendi");
      } else {
        toast.error(res.data.error || "Veri alinamadi");
      }
    } catch {
      toast.error("Hata olustu");
    } finally {
      setActionLoading((p) => ({ ...p, realtime: false }));
    }
  }, [deviceId]);

  useEffect(() => {
    fetchDetail();
    axios.get(`${API_URL}/api/devices/${deviceId}/realtime-data`).then((res) => {
      if (res.data.success) setRealtimeData(res.data);
    }).catch(() => {});
  }, [fetchDetail, deviceId]);

  const handleCircuitSwitch = async (enable) => {
    setActionLoading((p) => ({ ...p, circuit: true }));
    try {
      const res = await axios.post(`${API_URL}/api/devices/${deviceId}/circuit-switch?enable=${enable}`);
      if (res.data.success) {
        const confirmed = res.data.confirmed ? " (dogrulandi)" : "";
        toast.success(`Sayac ${enable ? "acildi" : "kapatildi"}${confirmed}`);
        fetchRealtimeData();
      } else {
        toast.error(res.data.error || "Islem basarisiz");
      }
    } catch { toast.error("Hata olustu"); }
    finally { setActionLoading((p) => ({ ...p, circuit: false })); }
  };

  const handlePrepaymentToggle = async (enable) => {
    setActionLoading((p) => ({ ...p, prepayment: true }));
    try {
      const res = await axios.post(`${API_URL}/api/devices/${deviceId}/prepayment-mode?enable=${enable}`);
      if (res.data.success) {
        toast.success(`On odeme modu ${enable ? "aktif" : "deaktif"}`);
        fetchRealtimeData();
      } else {
        toast.error("Islem basarisiz");
      }
    } catch { toast.error("Hata olustu"); }
    finally { setActionLoading((p) => ({ ...p, prepayment: false })); }
  };

  const handleBypassDetectionToggle = async (enable) => {
    setActionLoading((p) => ({ ...p, bypass: true }));
    try {
      const res = await axios.put(`${API_URL}/api/devices/${deviceId}/bypass-detection?enable=${enable}`);
      if (res.data.success) {
        toast.success(`Bypass algilama ${enable ? "aktif" : "pasif"} edildi`);
        fetchDetail();
      } else {
        toast.error("Islem basarisiz");
      }
    } catch { toast.error("Hata olustu"); }
    finally { setActionLoading((p) => ({ ...p, bypass: false })); }
  };

  const handleChargeEnergy = async () => {
    const kwh = parseFloat(chargeKwh);
    if (!kwh || kwh <= 0) { toast.error("Gecerli bir kWh degeri girin"); return; }
    setActionLoading((p) => ({ ...p, charge: true }));
    try {
      const res = await axios.post(`${API_URL}/api/devices/${deviceId}/charge-energy?kwh=${kwh}`);
      if (res.data.success) {
        const autoMsg = res.data.auto_on ? " Cihaz otomatik acildi!" : "";
        const warnMsg = res.data.auto_on_message ? ` (${res.data.auto_on_message})` : "";
        toast.success(`${kwh} kWh basariyla yuklendi!${autoMsg}${warnMsg}`);
        setChargeKwh("");
        fetchRealtimeData();
      } else {
        toast.error(res.data.error || "Yukleme basarisiz");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hata olustu");
    } finally { setActionLoading((p) => ({ ...p, charge: false })); }
  };

  const handleClearEnergy = async () => {
    if (!window.confirm("Sayac kWh bakiyesini sifirlamak istediginize emin misiniz?")) return;
    setActionLoading((p) => ({ ...p, clear: true }));
    try {
      const res = await axios.post(`${API_URL}/api/devices/${deviceId}/clear-energy`);
      if (res.data.success) {
        toast.success("Sayac bakiyesi sifirlandi");
        fetchRealtimeData();
      } else {
        toast.error(res.data.error || "Sifirlama basarisiz");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hata olustu");
    } finally { setActionLoading((p) => ({ ...p, clear: false })); }
  };

  const handleTopupPreview = async (amount) => {
    const tl = amount || parseFloat(topupTl);
    if (!tl || tl <= 0) { setTopupPreview(null); return; }
    setPreviewLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/devices/${deviceId}/topup-preview?tl_amount=${tl}`);
      setTopupPreview(res.data);
    } catch (err) {
      if (err.response?.status === 400) {
        toast.error(err.response.data.detail);
      }
      setTopupPreview(null);
    } finally { setPreviewLoading(false); }
  };

  const handleTopupConfirm = async () => {
    if (!topupPreview) return;
    setActionLoading((p) => ({ ...p, topup: true }));
    try {
      const res = await axios.post(`${API_URL}/api/devices/${deviceId}/topup-kwh?tl_amount=${topupPreview.tl_amount}`);
      if (res.data.success) {
        if (res.data.delivery_status === "pending_delivery") {
          toast.warning(
            `${res.data.kwh_loaded} kWh odemeniz alindi (${res.data.tl_deducted} TL). Cihaz offline — online oldugunda otomatik yuklenecek.`,
            { duration: 8000 }
          );
        } else {
          const autoMsg = res.data.auto_on ? " Cihaz otomatik acildi!" : "";
          toast.success(`${res.data.kwh_loaded} kWh yuklendi! (${res.data.tl_deducted} TL dusuldu)${autoMsg}`);
        }
        setTopupTl("");
        setTopupPreview(null);
        fetchDetail();
        fetchRealtimeData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Yukleme hatasi");
    } finally { setActionLoading((p) => ({ ...p, topup: false })); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Spinner size={32} className="animate-spin text-indigo-600" /></div>;
  }

  if (!detail) {
    return <div className="text-center py-16 text-slate-500">Cihaz bulunamadi</div>;
  }

  const { device, unit, apartment, tenant, tariff, remaining_kwh } = detail;
  const rt = realtimeData || {};

  return (
    <div className="space-y-6" data-testid="device-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft size={18} className="mr-1" /> Geri
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            {(() => {
              const rtOnline = rt.online !== undefined ? rt.online : device.online;
              const rtUpdate = rt.last_status_update || device.last_status_update;
              const freshness = getDeviceFreshness({ online: rtOnline, last_status_update: rtUpdate });
              const fd = getFreshnessDisplay(freshness);
              return (
                <>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${freshness === "fresh_online" ? "bg-emerald-100" : freshness === "stale_online" ? "bg-amber-50" : freshness === "offline" ? "bg-slate-100" : "bg-blue-50"}`}>
                    <Circuitry size={26} weight="duotone" className={fd.iconClass} />
                  </div>
                  <div>
                    <h1 className="text-2xl font-heading font-bold text-slate-900">{device.name}</h1>
                    {isAdmin && <p className="text-xs text-slate-400 font-mono">Seri No: {device.tuya_device_id}</p>}
                  </div>
                  <Badge
                    className={fd.badgeClass}
                    title={`Son guncelleme: ${timeAgoTr(rtUpdate)}`}
                    data-testid="online-status-badge"
                  >
                    <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${fd.dotClass}`} />
                    {fd.label}
                  </Badge>
                </>
              );
            })()}
            {device.is_active ? (
              <Badge className="bg-emerald-50 text-emerald-600 border border-emerald-200" data-testid="active-badge">Aktif</Badge>
            ) : (
              <Badge className="bg-slate-50 text-slate-500 border border-slate-200" data-testid="inactive-badge">Kapali</Badge>
            )}
            {device.pending_action && (
              <Badge className="bg-amber-100 text-amber-700 animate-pulse" data-testid="pending-action-badge">
                {device.pending_action === "reopen" ? "Acma Beklemede" :
                 device.pending_action === "cutoff" ? "Kesme Beklemede" :
                 device.pending_action === "turn_on" ? "Acma Beklemede" :
                 device.pending_action === "turn_off" ? "Kapatma Beklemede" : "Beklemede"}
              </Badge>
            )}
            {!device.pending_action && device.last_command_status === "failed" && (
              <Badge className="bg-red-100 text-red-600" data-testid="command-failed-badge">Son Komut Basarisiz</Badge>
            )}
            {rt.prepayment_mode && <Badge className="bg-indigo-100 text-indigo-700">On Odemeli</Badge>}
          </div>
        </div>
        <Button variant="outline" onClick={fetchRealtimeData} disabled={actionLoading.realtime} data-testid="refresh-btn">
          {actionLoading.realtime ? <Spinner size={14} className="animate-spin mr-2" /> : <ArrowsClockwise size={14} className="mr-2" />}
          Guncelle
        </Button>
      </div>

      {/* Live Data Cards */}
      {rt.online === false && (
        <Card className="border-red-200 bg-red-50/50 shadow-sm" data-testid="offline-warning">
          <CardContent className="p-4 flex items-center gap-3">
            <Warning size={20} className="text-red-600" />
            <div>
              <p className="text-sm text-red-700 font-medium">
                Cihaz offline! Komut gonderilemez. Lutfen cihazin internete bagli oldugundan emin olun.
              </p>
              {device.pending_action && (
                <p className="text-xs text-red-500 mt-1">
                  Bekleyen islem: {device.pending_action === "reopen" ? "Sayac Acma" :
                   device.pending_action === "cutoff" ? "Elektrik Kesme" :
                   device.pending_action === "turn_on" ? "Cihaz Acma" : "Cihaz Kapatma"}
                  {device.pending_action_reason ? ` — ${device.pending_action_reason}` : ""}
                  . Cihaz online oldugunda otomatik denenecek.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-amber-500 mb-1">Voltaj</p>
            <p className="text-2xl font-bold text-slate-900" data-testid="voltage">{rt.voltage ?? device.voltage ?? 0} <span className="text-sm font-normal text-slate-400">V</span></p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-blue-500 mb-1">Akim</p>
            <p className="text-2xl font-bold text-slate-900" data-testid="current-a">{rt.current ?? device.current_a ?? 0} <span className="text-sm font-normal text-slate-400">A</span></p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-indigo-500 mb-1">Anlik Guc</p>
            <p className="text-2xl font-bold text-slate-900" data-testid="current-power">{rt.power ?? device.current_power_w ?? 0} <span className="text-sm font-normal text-slate-400">W</span></p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-emerald-500 mb-1">Toplam Tuketim</p>
            <p className="text-2xl font-bold text-slate-900" data-testid="total-energy">{rt.total_energy_kwh ?? device.total_energy_kwh ?? 0} <span className="text-sm font-normal text-slate-400">kWh</span></p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-1 mb-1">
              <Thermometer size={12} className="text-orange-500" />
              <p className="text-xs font-medium text-orange-500">Sicaklik</p>
            </div>
            <p className="text-2xl font-bold text-slate-900" data-testid="temperature">{rt.temperature ?? device.temperature ?? 0} <span className="text-sm font-normal text-slate-400">°C</span></p>
          </CardContent>
        </Card>
      </div>

      {/* kWh Balance + Topup Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Sayac kWh Bakiye */}
        <Card className={`shadow-sm ${(rt.balance_energy_kwh || 0) <= 1 ? "border-red-300 bg-red-50/50" : "border-emerald-200 bg-emerald-50/30"}`}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <p className={`text-xs font-medium ${(rt.balance_energy_kwh || 0) <= 1 ? "text-red-500" : "text-emerald-500"}`}>Sayac kWh Bakiyesi</p>
              <BatteryCharging size={18} className={(rt.balance_energy_kwh || 0) <= 1 ? "text-red-400" : "text-emerald-400"} />
            </div>
            <p className={`text-3xl font-bold ${(rt.balance_energy_kwh || 0) <= 1 ? "text-red-700" : "text-slate-900"}`} data-testid="balance-kwh">
              {rt.balance_energy_kwh ?? device.balance_energy_kwh ?? 0} <span className="text-base font-normal text-slate-400">kWh</span>
            </p>
            {rt.leakage_current > 0 && (
              <div className="mt-2 flex items-center gap-1 text-xs text-red-600">
                <Drop size={12} /> Kacak akim: {rt.leakage_current} mA
              </div>
            )}
            {hasPerm("topup_clear") && (
              <Button variant="outline" size="sm" className="mt-3 w-full text-red-600 hover:text-red-700 border-red-200"
                onClick={handleClearEnergy} disabled={actionLoading.clear} data-testid="clear-energy-btn">
                {actionLoading.clear ? <Spinner size={14} className="animate-spin mr-2" /> : <Warning size={14} className="mr-2" />}
                Bakiyeyi Sifirla
              </Button>
            )}
          </CardContent>
        </Card>

        {/* TL → kWh Kademeli Yukleme */}
        {hasPerm("topup_load") && tenant && !isStaff && (
        <Card className="border-indigo-200 bg-indigo-50/30 shadow-sm col-span-1 lg:col-span-2">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-indigo-700">TL → kWh Kademeli Yukleme</p>
                <p className="text-xs text-slate-500">{t("common.tariff")}: {tariff?.name || "-"}
                  {topupPreview && ` | ${t("topup.this_month")}: ${topupPreview.monthly_kwh_loaded} kWh (${t("topup.tier")} ${topupPreview.current_tier})`}
                </p>
              </div>
              <Gauge size={20} className="text-indigo-400" />
            </div>

            <div className="flex gap-2 mb-3">
              {[50, 100, 250, 500, 1000].map((val) => (
                <Button key={val} size="sm" variant="outline" className="flex-1 text-xs"
                  onClick={() => { setTopupTl(val.toString()); handleTopupPreview(val); }}
                  data-testid={`topup-preset-${val}`}>
                  {"\u20ba"}{val}
                </Button>
              ))}
            </div>
            <div className="flex gap-2 mb-3">
              <Input type="number" placeholder="TL miktari" value={topupTl}
                onChange={(e) => setTopupTl(e.target.value)}
                className="flex-1" data-testid="topup-tl-input" />
              <Button variant="outline" onClick={() => handleTopupPreview()} disabled={previewLoading} data-testid="topup-preview-btn">
                {previewLoading ? <Spinner size={14} className="animate-spin" /> : "Hesapla"}
              </Button>
            </div>

            {/* Preview Breakdown */}
            {topupPreview && (
              <div className="space-y-3 mb-3">
                {/* Debt Warning */}
                {topupPreview.debt_amount > 0 && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl" data-testid="debt-warning">
                    <p className="text-xs font-medium text-red-700">Odenmemis Borc: {"\u20ba"}{topupPreview.debt_amount.toFixed(2)}</p>
                    <p className="text-xs text-red-600">Bu odemeden {"\u20ba"}{topupPreview.debt_deducted.toFixed(2)} borc icin kesilecek, kalan {"\u20ba"}{topupPreview.effective_tl.toFixed(2)} kWh icin kullanilacak</p>
                  </div>
                )}
                {/* Tier Warning */}
                {topupPreview.tier_warning && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl" data-testid="tier-warning">
                    <p className="text-xs font-medium text-amber-700">{topupPreview.tier_warning}</p>
                    <p className="text-xs text-amber-600">Mevcut: Kademe {topupPreview.current_tier} | Sonrasi: Kademe {topupPreview.after_tier}</p>
                  </div>
                )}
                <div className="border border-indigo-200 rounded-xl overflow-hidden" data-testid="topup-breakdown">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-indigo-100/50">
                      <th className="text-left p-2 font-medium text-indigo-700">Kademe</th>
                      <th className="text-left p-2 font-medium text-indigo-700">Aralik</th>
                      <th className="text-right p-2 font-medium text-indigo-700">Birim Fiyat</th>
                      <th className="text-right p-2 font-medium text-indigo-700">kWh</th>
                      <th className="text-right p-2 font-medium text-indigo-700">{t("common.amount")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topupPreview.breakdown.map((b, i) => (
                      <tr key={i} className="border-t border-indigo-100">
                        <td className="p-2 text-slate-700">{b.tier}. Kademe</td>
                        <td className="p-2 text-slate-600">{b.range}</td>
                        <td className="p-2 text-right text-slate-700">{"\u20ba"}{b.price_per_kwh}</td>
                        <td className="p-2 text-right font-medium text-slate-900">{b.kwh}</td>
                        <td className="p-2 text-right font-medium text-slate-900">{"\u20ba"}{b.cost}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-indigo-300 bg-indigo-50">
                      <td colSpan={3} className="p-2 font-semibold text-indigo-700">{t("common.total")}</td>
                      <td className="p-2 text-right font-bold text-indigo-900">{topupPreview.total_kwh} kWh</td>
                      <td className="p-2 text-right font-bold text-indigo-900">{"\u20ba"}{topupPreview.total_cost}</td>
                    </tr>
                  </tfoot>
                </table>
                </div>
              </div>
            )}

            {topupPreview && (
              <Button onClick={handleTopupConfirm} disabled={actionLoading.topup}
                className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="topup-confirm-btn">
                {actionLoading.topup ? <Spinner size={16} className="animate-spin mr-2" /> : <Plus size={16} className="mr-2" />}
                {topupPreview.total_kwh} kWh Yukle ({"\u20ba"}{topupPreview.total_cost})
                {topupPreview.debt_deducted > 0 ? ` + ${"\u20ba"}${topupPreview.debt_deducted.toFixed(2)} borc` : ""}
              </Button>
            )}
          </CardContent>
        </Card>
        )}

        {/* Staff topup redirect */}
        {isStaff && hasPerm("topup_load") && tenant && (
          <Card className="border-amber-200 bg-amber-50/30 shadow-sm col-span-1 lg:col-span-2">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <CurrencyCircleDollar size={22} className="text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-amber-800">kWh Yukleme</p>
                  <p className="text-xs text-amber-600">Yukleme islemleri kiracı sayfasindan yapilmalidir</p>
                </div>
              </div>
              <Button onClick={() => navigate("/tenants")} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="go-tenants-topup-btn">
                <User size={16} className="mr-2" /> Kiracılar Sayfasına Git
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Direct kWh charge (admin only) */}
        {!tenant && hasPerm("topup_load") && (
        <Card className="border-slate-200 shadow-sm col-span-1">
          <CardContent className="p-5">
            <p className="text-xs font-medium text-slate-500 mb-3">Dogrudan kWh Yukle</p>
            <div className="flex gap-2">
              <Input type="number" placeholder="kWh" value={chargeKwh}
                onChange={(e) => setChargeKwh(e.target.value)}
                className="flex-1" data-testid="charge-kwh-input" />
              <Button onClick={handleChargeEnergy} disabled={actionLoading.charge}
                className="bg-slate-700 hover:bg-slate-800" data-testid="charge-btn">
                {actionLoading.charge ? <Spinner size={14} className="animate-spin" /> : <Plus size={14} className="mr-1" />}
                Yukle
              </Button>
            </div>
          </CardContent>
        </Card>
        )}

        {/* TL Balance info when tenant exists but no charge permission */}
        {tenant && !hasPerm("topup_load") && (
          <Card className="border-slate-200 shadow-sm col-span-1">
            <CardContent className="p-5">
              <p className="text-xs font-medium text-slate-500 mb-2">TL Bakiye</p>
              <p className="text-2xl font-bold text-slate-900">{"\u20ba"}{tenant.balance.toFixed(2)}</p>
              {remaining_kwh > 0 && tariff && (
                <p className="text-sm text-slate-500 mt-1">= ~{remaining_kwh} kWh ({tariff.name})</p>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Location + Controls */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Konum ve Kiraci Bilgisi</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {apartment && (
              <div className="flex items-center gap-2 text-sm">
                <Buildings size={16} className="text-slate-400" />
                <span className="text-slate-600">Apartman:</span>
                <span className="font-medium text-slate-900">{apartment.name}</span>
              </div>
            )}
            {unit ? (
              <div className="flex items-center gap-2 text-sm">
                <House size={16} className="text-slate-400" />
                <span className="text-slate-600">Daire:</span>
                <span className="font-medium text-slate-900">{unit.name}</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-amber-600">
                <Warning size={16} />
                <span className="font-medium">Daireye atanmamis</span>
              </div>
            )}
            {tenant && (
              <>
                <div className="flex items-center gap-2 text-sm">
                  <User size={16} className="text-slate-400" />
                  <span className="text-slate-600">Kiraci:</span>
                  <span className="font-medium text-slate-900">{tenant.name}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <CurrencyCircleDollar size={16} className="text-slate-400" />
                  <span className="text-slate-600">Bakiye:</span>
                  <Badge className={tenant.balance > 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>
                    {"\u20ba"}{tenant.balance.toFixed(2)}
                  </Badge>
                </div>
              </>
            )}
            {tariff && (
              <div className="flex items-center gap-2 text-sm">
                <CurrencyCircleDollar size={16} className="text-slate-400" />
                <span className="text-slate-600">Tarife:</span>
                <span className="font-medium text-slate-900">{tariff.name} ({tariff.tiers.length} kademe)</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Sayac Kontrol</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {hasPerm("device_control") && (
            <div className="grid grid-cols-2 gap-3">
              <Button onClick={() => handleCircuitSwitch(true)} disabled={actionLoading.circuit}
                className="bg-emerald-600 hover:bg-emerald-700" data-testid="circuit-on-btn">
                {actionLoading.circuit ? <Spinner size={16} className="animate-spin mr-2" /> : <Power size={16} className="mr-2" />}
                Sayaci Ac
              </Button>
              <Button onClick={() => handleCircuitSwitch(false)} disabled={actionLoading.circuit}
                variant="destructive" data-testid="circuit-off-btn">
                {actionLoading.circuit ? <Spinner size={16} className="animate-spin mr-2" /> : <Power size={16} className="mr-2" />}
                Sayaci Kapat
              </Button>
            </div>
            )}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
              <div>
                <p className="text-sm font-medium text-slate-900">Sayac Durumu</p>
                <p className="text-xs text-slate-500">
                  {rt.switch !== undefined ? (rt.switch ? t("devices.meter_on") : t("devices.meter_off")) : t("common.unknown")}
                </p>
              </div>
              <Badge className={rt.switch ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>
                {rt.switch ? t("common.on") : t("common.off")}
              </Badge>
            </div>
            {hasPerm("device_control") && (
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
              <div>
                <p className="text-sm font-medium text-slate-900">On Odeme Modu</p>
                <p className="text-xs text-slate-500">Bakiye bitince sayac otomatik kapanir</p>
              </div>
              <Switch
                checked={rt.prepayment_mode ?? false}
                onCheckedChange={handlePrepaymentToggle}
                disabled={actionLoading.prepayment}
                data-testid="prepayment-switch"
              />
            </div>
            )}
            {hasPerm("device_control") && (
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
              <div>
                <p className="text-sm font-medium text-slate-900">Bypass Algilama</p>
                <p className="text-xs text-slate-500">Kacak elektrik kullanimini tespit eder</p>
              </div>
              <Switch
                checked={device.bypass_detection_enabled !== false}
                onCheckedChange={handleBypassDetectionToggle}
                disabled={actionLoading.bypass}
                data-testid="bypass-detection-switch"
              />
            </div>
            )}
            {rt.fault > 0 && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2">
                <Warning size={18} className="text-red-600" />
                <p className="text-sm text-red-700 font-medium">Ariza kodu: {rt.fault}</p>
              </div>
            )}
            {/* Alarm Section */}
            {rt.alarms && rt.alarms.length > 0 && (
              <div className="space-y-2" data-testid="alarms-section">
                <p className="text-sm font-semibold text-slate-700">Alarmlar</p>
                {rt.alarms.map((alarm, idx) => (
                  <div key={idx} className={`p-3 rounded-xl flex items-center gap-2 border ${
                    alarm.severity === "critical" ? "bg-red-50 border-red-200" :
                    alarm.severity === "warning" ? "bg-amber-50 border-amber-200" :
                    "bg-blue-50 border-blue-200"
                  }`} data-testid={`alarm-item-${idx}`}>
                    <Warning size={16} className={
                      alarm.severity === "critical" ? "text-red-600" :
                      alarm.severity === "warning" ? "text-amber-600" : "text-blue-600"
                    } />
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${
                        alarm.severity === "critical" ? "text-red-700" :
                        alarm.severity === "warning" ? "text-amber-700" : "text-blue-700"
                      }`}>{alarm.type}</p>
                      {alarm.threshold !== undefined && (
                        <p className="text-xs text-slate-500">
                          Esik: {alarm.threshold} | {alarm.enabled ? t("common.active") : t("common.inactive")}
                        </p>
                      )}
                    </div>
                    <Badge className={
                      alarm.severity === "critical" ? "bg-red-100 text-red-700" :
                      alarm.severity === "warning" ? "bg-amber-100 text-amber-700" :
                      "bg-blue-100 text-blue-700"
                    }>{alarm.severity === "critical" ? t("common.critical") : alarm.severity === "warning" ? t("common.warning") : t("common.info")}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DeviceDetailPage;
